<template>
  <section class="opening-hours" :class="{ 'opening-hours--modal': modal, 'opening-hours--simple': simpleMode }" :style="colorVars" :aria-labelledby="headingId">
    <header class="opening-hours__header">
      <div>
        <span class="opening-hours__eyebrow">{{ eyebrow || "PLANIFICA TU VISITA" }}</span>
        <h2 :id="headingId">{{ title || "Horarios de atención" }}</h2>
        <p>{{ text || "Consulta cuándo puedes visitarnos o realizar tu pedido." }}</p>
      </div>
      <button v-if="modal" type="button" class="opening-hours__close" aria-label="Cerrar horario" @click="$emit('close')">
        <b-icon icon="x" aria-hidden="true" />
      </button>
    </header>

    <div v-if="days.length" class="opening-hours__content">
      <aside class="opening-hours__today">
        <span class="opening-hours__today-label">{{ todayLabel || "HOY" }} · {{ todayName }}</span>
        <b-icon icon="clock" class="opening-hours__today-icon" aria-hidden="true" />
        <strong>{{ todayStatus }}</strong>
        <p>{{ todayHours }}</p>
        <small v-if="todayDay && todayDay.breakEnabled && todayDay.breakStart && todayDay.breakEnd">
          {{ pauseLabel || "Pausa" }}: {{ formatTime(todayDay.breakStart) }} a {{ formatTime(todayDay.breakEnd) }}
        </small>
      </aside>

      <div v-if="!simpleMode" class="opening-hours__week">
        <div class="opening-hours__week-heading">
          <span>{{ weekLabel || "HORARIO SEMANAL" }}</span>
          <b-icon icon="calendar3" aria-hidden="true" />
        </div>
        <ul>
          <li v-for="day in days" :key="day.n" :class="{ 'is-today': Number(day.n) === todayIndex }">
            <span class="opening-hours__day">
              {{ day.name || dayNames[Number(day.n)] }}
              <small v-if="Number(day.n) === todayIndex">{{ todayLabel || "HOY" }}</small>
            </span>
            <span class="opening-hours__time">
              <template v-if="hasHours(day)">
                {{ formatTime(day.openAt) }} a {{ formatTime(day.closeAt) }}
                <small v-if="day.breakEnabled && day.breakStart && day.breakEnd">
                  {{ pauseLabel || "Pausa" }} {{ formatTime(day.breakStart) }}–{{ formatTime(day.breakEnd) }}
                </small>
              </template>
              <span v-else class="opening-hours__closed">{{ closedLabel || "Cerrado" }}</span>
            </span>
          </li>
        </ul>
      </div>
    </div>

    <div v-else class="opening-hours__empty">
      <b-icon icon="calendar-x" aria-hidden="true" />
      <strong>{{ emptyTitle || "Horario no disponible" }}</strong>
      <p>{{ emptyText || "El local aún no ha publicado sus horarios de atención." }}</p>
    </div>
  </section>
</template>

<script>
export default {
  props: {
    title: { type: String, default: "" },
    text: { type: String, default: "" },
    modal: { type: Boolean, default: false },
    hoursMode: { type: String, default: "full" },
    eyebrow: { type: String, default: "" },
    todayLabel: { type: String, default: "" },
    weekLabel: { type: String, default: "" },
    pauseLabel: { type: String, default: "" },
    closedLabel: { type: String, default: "" },
    noTodayHoursText: { type: String, default: "" },
    temporaryClosedText: { type: String, default: "" },
    opensLaterText: { type: String, default: "" },
    closedTodayText: { type: String, default: "" },
    onBreakText: { type: String, default: "" },
    openStatusText: { type: String, default: "" },
    emptyTitle: { type: String, default: "" },
    emptyText: { type: String, default: "" },
    accentColor: { type: String, default: "" },
    todayColor: { type: String, default: "" },
    todayTextColor: { type: String, default: "" },
    backgroundColor: { type: String, default: "" },
    headingColor: { type: String, default: "" },
  },
  data() {
    return {
      now: Date.now(),
      clockTimer: null,
      dayNames: ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"],
    };
  },
  computed: {
    colorVars() {
      return {
        ...(this.accentColor && { "--hours-accent": this.accentColor }),
        ...(this.todayColor && { "--hours-today": this.todayColor }),
        ...(this.todayTextColor && { "--hours-today-text": this.todayTextColor }),
        ...(this.backgroundColor && { "--hours-background": this.backgroundColor }),
        ...(this.headingColor && { "--hours-ink": this.headingColor }),
      };
    },
    simpleMode() {
      return this.hoursMode === "simple";
    },
    headingId() {
      return `opening-hours-title-${this._uid}`;
    },
    settings() {
      const info = this.$store.state.info || {};
      return info.settings || {};
    },
    days() {
      const days = Array.isArray(this.settings.days) ? this.settings.days : [];
      return days.slice().sort((a, b) => (Number(a.n) + 6) % 7 - (Number(b.n) + 6) % 7);
    },
    todayIndex() {
      return Number(this.$moment(this.now).format("d"));
    },
    todayDay() {
      return this.days.find((day) => Number(day.n) === this.todayIndex);
    },
    todayName() {
      return this.dayNames[this.todayIndex];
    },
    todayHours() {
      if (!this.hasHours(this.todayDay)) return this.noTodayHoursText || "Sin atención programada para hoy";
      return `${this.formatTime(this.todayDay.openAt)} a ${this.formatTime(this.todayDay.closeAt)}`;
    },
    todayStatus() {
      const day = this.todayDay;
      if (!this.hasHours(day)) return this.closedTodayText || "Cerrado hoy";

      if (this.settings.closedUntil && this.$moment(this.settings.closedUntil).isAfter(this.now)) {
        return this.temporaryClosedText || "Cerrado temporalmente";
      }

      const current = this.$moment(this.now);
      const minutes = current.hours() * 60 + current.minutes();
      const open = this.timeToMinutes(day.openAt);
      const close = this.timeToMinutes(day.closeAt);
      if (minutes < open) return this.opensLaterText || "Abre más tarde";
      if (minutes >= close) return this.closedTodayText || "Cerrado por hoy";

      if (day.breakEnabled && day.breakStart && day.breakEnd) {
        const breakStart = this.timeToMinutes(day.breakStart);
        const breakEnd = this.timeToMinutes(day.breakEnd);
        if (minutes >= breakStart && minutes < breakEnd) return this.onBreakText || "En pausa";
      }

      return this.openStatusText || "Dentro del horario";
    },
  },
  mounted() {
    this.clockTimer = setInterval(() => {
      this.now = Date.now();
    }, 60000);
  },
  beforeDestroy() {
    if (this.clockTimer) clearInterval(this.clockTimer);
  },
  methods: {
    timeToMinutes(value) {
      if (!value) return null;
      const [hours, minutes] = String(value).split(":").map(Number);
      if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
      return hours * 60 + minutes;
    },
    hasHours(day) {
      return !!(day && this.timeToMinutes(day.openAt) !== null && this.timeToMinutes(day.closeAt) !== null);
    },
    formatTime(value) {
      return String(value || "").slice(0, 5);
    },
  },
};
</script>

<style scoped>
.opening-hours {
  --hours-ink: #223c40;
  --hours-accent: #147b69;
  --hours-today: #147b69;
  --hours-today-text: #fff;
  --hours-background: #fff;
  --hours-muted: color-mix(in srgb, var(--hours-ink) 70%, var(--hours-background));
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--hours-accent) 18%, var(--hours-background));
  border-radius: 24px;
  background: var(--hours-background);
  color: var(--hours-ink);
  box-shadow: 0 18px 44px rgba(20, 65, 65, 0.09);
}

.opening-hours--modal {
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

.opening-hours__header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: clamp(22px, 3vw, 34px);
  background: radial-gradient(circle at 95% 0%, color-mix(in srgb, var(--hours-accent) 20%, transparent), transparent 38%), linear-gradient(135deg, color-mix(in srgb, var(--hours-accent) 6%, var(--hours-background)), var(--hours-background));
}

.opening-hours__eyebrow,
.opening-hours__today-label,
.opening-hours__week-heading {
  font-size: 0.68rem;
  font-weight: 850;
  letter-spacing: 0.15em;
}

.opening-hours__eyebrow { color: var(--hours-accent); }

.opening-hours__header h2 {
  margin: 7px 0 6px;
  color: var(--hours-ink);
  font-size: clamp(1.45rem, 2.8vw, 2rem);
  font-weight: 850;
  letter-spacing: -0.04em;
  line-height: 1.12;
}

.opening-hours__header p {
  margin: 0;
  color: var(--hours-muted);
  font-size: 0.9rem;
}

.opening-hours__close {
  display: grid;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  place-items: center;
  border: 1px solid color-mix(in srgb, var(--hours-accent) 20%, var(--hours-background));
  border-radius: 50%;
  background: var(--hours-background);
  color: var(--hours-ink);
  font-size: 1.25rem;
}

.opening-hours__close:hover,
.opening-hours__close:focus-visible {
  background: color-mix(in srgb, var(--hours-accent) 10%, var(--hours-background));
}

.opening-hours__content {
  display: grid;
  grid-template-columns: minmax(210px, 0.9fr) minmax(0, 1.4fr);
  gap: clamp(18px, 3vw, 32px);
  padding: clamp(18px, 3vw, 30px);
}

.opening-hours--simple .opening-hours__content { display: block; padding-top: 0; }
.opening-hours--simple .opening-hours__today { min-height: 145px; }
.opening-hours--simple .opening-hours__today-icon { margin: 18px 0 10px; }

.opening-hours__today {
  display: flex;
  min-height: 230px;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px;
  border-radius: 18px;
  background: linear-gradient(155deg, color-mix(in srgb, var(--hours-today) 78%, #000), color-mix(in srgb, var(--hours-today) 88%, #fff));
  color: var(--hours-today-text);
}

.opening-hours__today-label { color: color-mix(in srgb, var(--hours-today-text) 76%, transparent); }
.opening-hours__today-icon { margin: auto 0 13px; font-size: 1.8rem; }

.opening-hours__today strong {
  font-size: 1.45rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.1;
}

.opening-hours__today p { margin: 8px 0 0; font-size: 0.92rem; }
.opening-hours__today small { margin-top: 7px; color: color-mix(in srgb, var(--hours-today-text) 78%, transparent); }

.opening-hours__week-heading {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 2px 0 11px;
  color: var(--hours-accent);
}

.opening-hours__week ul { margin: 0; padding: 0; list-style: none; }

.opening-hours__week li {
  display: flex;
  min-height: 44px;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 9px 12px;
  border-top: 1px solid color-mix(in srgb, var(--hours-ink) 12%, var(--hours-background));
  border-radius: 8px;
  font-size: 0.85rem;
}

.opening-hours__week li.is-today {
  background: color-mix(in srgb, var(--hours-accent) 11%, var(--hours-background));
  color: var(--hours-accent);
  font-weight: 800;
}

.opening-hours__day {
  display: flex;
  align-items: center;
  gap: 7px;
  font-weight: 700;
}

.opening-hours__day small {
  padding: 3px 6px;
  border-radius: 5px;
  background: color-mix(in srgb, var(--hours-accent) 18%, var(--hours-background));
  color: var(--hours-accent);
  font-size: 0.58rem;
}

.opening-hours__time { color: var(--hours-muted); text-align: right; }
.opening-hours__time small { display: block; margin-top: 2px; color: var(--hours-muted); font-size: 0.72rem; }
.opening-hours__closed { color: var(--hours-muted); }

.opening-hours__empty {
  display: flex;
  min-height: 190px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 28px;
  color: var(--hours-muted);
  text-align: center;
}

.opening-hours__empty > svg { margin-bottom: 10px; color: var(--hours-accent); font-size: 1.8rem; }
.opening-hours__empty strong { color: var(--hours-ink); }
.opening-hours__empty p { margin: 6px 0 0; font-size: 0.85rem; }

@media (max-width: 650px) {
  .opening-hours__content { grid-template-columns: minmax(0, 1fr); }
  .opening-hours__today { min-height: 170px; padding: 20px; }
  .opening-hours__today-icon { margin-top: 22px; }
}

@media (max-width: 400px) {
  .opening-hours__week li { align-items: flex-start; gap: 6px; font-size: 0.8rem; }
  .opening-hours__day { flex-wrap: wrap; }
}
</style>
